<template>
  <nav
    v-if="totalPages > 1"
    :class="[$style.guggenPagination, 'space-36-48-above']"
    role="navigation"
    aria-label="Pagination Navigation"
  >
    <ul>
      <li v-if="!isInFirstPage" :class="$style.previous">
        <a
          tabindex="0"
          aria-label="Go to previous page"
          :ga4-event="
            JSON.stringify({
              click_type: 'navigation',
              component: 'GuggenPagination',
              content_group: `${parent}`,
              gtm_tag: 'navigation',
              link_text: 'Previous',
              link_url: `${currentPage}-of-${totalPages}`,
            })
          "
          @click.prevent="onClickPreviousPage()"
          @keyup.enter="onClickPreviousPage()"
        >
          Previous
        </a>
      </li>
      <li
        v-if="!isInFirstPage && totalPages > 5 && currentPage > 2 && !isMobile()"
        :class="$style.ellipsis"
      >
        &hellip;
      </li>
      <li
        v-for="(page, key) in pages"
        :key="key"
        :class="[
          { [$style.hidden]: isMobile() && !isPageActive(page.name) },
          { [$style.last]: key + 1 === pages.length },
        ]"
      >
        <a
          :disabled="page.isDisabled"
          :class="[{ [$style.active]: isPageActive(page.name) }]"
          :aria-current="isPageActive(page.name) ? 'page' : false"
          :aria-label="`Go to page number ${page.name}`"
          :ga4-event="
            JSON.stringify({
              click_type: 'navigation',
              component: 'GuggenPagination',
              content_group: `${parent}`,
              gtm_tag: 'navigation',
              link_text: `page-${page.name}`,
              link_url: `${currentPage}-of-${totalPages}`,
            })
          "
          tabindex="0"
          role="link"
          @click.prevent="onClickPage(page.name)"
          @keyup.enter="onClickPage(page.name)"
        >
          {{ page.name }}
        </a>
      </li>
      <li
        v-if="!isInLastPage && totalPages > 5 && totalPages - currentPage > 3 && !isMobile()"
        :class="$style.ellipsis"
      >
        &hellip;
      </li>
      <li
        v-if="!isInLastPage && totalPages > 5 && totalPages - currentPage > 3 && !isMobile()"
        :class="$style.total"
      >
        <a
          :disabled="currentPage === totalPages"
          :class="[{ [$style.active]: isPageActive(totalPages) }]"
          :aria-current="isPageActive(totalPages) ? 'page' : false"
          :aria-label="`Go to page number ${totalPages}`"
          :ga4-event="
            JSON.stringify({
              click_type: 'navigation',
              component: 'GuggenPagination',
              content_group: `${parent}`,
              gtm_tag: 'navigation',
              link_text: `page-${totalPages}}`,
              link_url: `${currentPage}-of-${totalPages}`,
            })
          "
          tabindex="0"
          role="link"
          @click.prevent="onClickPage(totalPages)"
          @keyup.enter="onClickPage(totalPages)"
        >
          {{ totalPages }}
        </a>
      </li>
      <li v-if="!isInLastPage" :class="$style.next">
        <a
          tabindex="0"
          aria-label="Go to next page"
          :ga4-event="
            JSON.stringify({
              click_type: 'navigation',
              component: 'GuggenPagination',
              content_group: `${parent}`,
              gtm_tag: 'navigation',
              link_text: 'Next',
              link_url: `${currentPage}-of-${totalPages}`,
            })
          "
          @click.prevent="onClickNextPage()"
          @keyup.enter="onClickNextPage()"
        >
          Next
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRouteParent, useScreenSize } from '@/composables/Common.js';

interface Page {
  name: number;
  isDisabled: boolean;
}

const props = withDefaults(
  defineProps<{
    bootstrapTotal?: number;
    currentPage: number;
    filter?: string;
    hasBootstrap?: boolean;
    maxButtons?: number;
    perPage?: number;
    postType: string;
    query?: string;
    subCategory?: string;
    subType?: string;
    subTypeValue?: string;
    totalPageCount?: number;
    totalSearchCount?: number;
  }>(),
  {
    currentPage: 1,
    filter: '',
    hasBootstrap: false,
    maxButtons: 5,
    perPage: 27,
    postType: 'post',
  }
);

const router = useRouter();
const { isMobile } = useScreenSize();
const { parent } = useRouteParent();

const slug = ref(getSlug());

const endPage = computed(() => Math.min(startPage.value + props.maxButtons - 1, totalPages.value));
const isInFirstPage = computed(() => props.currentPage === 1);
const isInLastPage = computed(() => props.currentPage === totalPages.value);
const pages = computed(() => {
  const range = [] as Array<Page>;

  for (let i = startPage.value; i <= endPage.value; i += 1) {
    range.push({
      name: i,
      isDisabled: i === props.currentPage,
    });
  }

  return range;
});
const startPage = computed(() => {
  if (props.currentPage === 1) {
    return 1;
  }

  if (props.currentPage === totalPages.value && totalPages.value > props.maxButtons) {
    return totalPages.value - props.maxButtons + 1;
  }

  return props.currentPage - 1;
});
const totalPages = computed(() => props.totalPageCount || 0);

function getSlug(): string {
  let sub;
  let type;

  switch (props.postType) {
    case 'all_lta':
      sub = 'curriculum';
      type = 'learning-through-art';
      break;
    case 'look_at_art':
    case 'making_art':
      sub = props.postType === 'look_at_art' ? 'art-investigation' : 'art-making';
      type = 'learning-through-art';
      break;
    case 'post':
      type = 'articles';
      break;
    default:
      type = prepareType(props.postType);
      break;
  }

  if (props.postType === 'exhibition' && props.filter === 'past') {
    type = 'exhibitions/past';
  }

  if (props.subType && props.subTypeValue) {
    if (props.subCategory) {
      return `/${type}/${props.subType}/${props.subTypeValue}/${props.subCategory}`;
    }

    if (props.postType === 'look_at_art' || props.postType === 'making_art') {
      return `/learning-through-art/${sub}/${props.subType}/${props.subTypeValue}`;
    }

    return `/${type}/${props.subType}/${props.subTypeValue}`;
  }

  if ((sub || props.subType) && props.subType !== 'all_audio' && type !== 'search') {
    sub = sub || props.subType;

    if (type === 'audio' && props.subType) {
      sub = props.subType.split('_')[1];
    }

    return `/${type}/${sub}`;
  }

  if (type === 'search') {
    if (props.subType) {
      return `/${type}?s=${props.query}&post_type=${props.subType}&page=`;
    }

    return `/${type}?s=${props.query}&page=`;
  }

  return `/${type}`;
}

function isPageActive(page: number): boolean {
  return props.currentPage === page;
}

function onClickNextPage(): void {
  pagechanged(props.currentPage + 1);
}

function onClickPage(page: number): void {
  pagechanged(page);
}

function onClickPreviousPage(): void {
  pagechanged(props.currentPage - 1);
}

function pagechanged(page: number): void {
  if (props.postType === 'search') {
    router.push({ path: `${slug.value}${page}` });
  } else if (page === 1) {
    if (props.postType === 'exhibition' && props.filter === 'past') {
      router.push({
        name: props.subType ? 'ExhibitionsPastTerm' : 'ExhibitionsPast',
        params: { past: 'true', term: props.subTypeValue, type: props.subType },
      });
    } else {
      router.push({ path: slug.value });
    }
  } else {
    if (props.postType === 'exhibition' && props.filter === 'past') {
      router.push({
        name: props.subType ? 'ExhibitionsPastTermPage' : 'ExhibitionsPastPage',
        params: { page: page, past: 'true', term: props.subTypeValue, type: props.subType },
      });
    } else {
      router.push({ path: `${slug.value}/page/${page}` });
    }
  }
}

function prepareType(type: string): string {
  return type.replace('_', '-');
}
</script>

<style lang="scss" module>
.guggenPagination {
  grid-column: 3 / span 8;

  ul {
    margin-left: 0;
    margin-top: 0;
    padding-left: 0;
    text-align: center;
  }

  li {
    display: inline;
    list-style-type: none;
    margin-right: 2rem;

    // Unset style polluting global scope.
    &:before {
      content: none;
    }

    a,
    span {
      @include heading-5;

      & {
        display: inline;
        margin-top: 0;
      }
    }

    a {
      cursor: pointer;
      text-decoration: none;

      &.active {
        border-bottom: 4px solid;
        pointer-events: none;
      }

      &.disabled {
        display: none;
      }

      &:hover {
        border-bottom: 4px solid;
      }

      &:focus {
        color: $white;
      }
    }

    &.hidden {
      display: none;
    }
  }

  .next,
  .previous {
    &:hover {
      a {
        border-bottom: none;
        cursor: pointer;
        text-decoration: underline;
      }
    }

    a {
      padding: 0.75rem;

      &:focus {
        color: $white;
      }
    }
  }

  .next {
    a {
      @include right-arrow;
    }
  }

  .previous {
    a {
      @include left-arrow;
    }
  }
}

@media (min-width: $break-point) {
  .guggenPagination {
    li {
      margin-right: 1.75rem;
    }

    .ellipsis,
    .last {
      margin-right: 1rem;
    }

    .previous {
      margin-right: 0.25rem;
    }

    .total {
      margin-right: 0.25rem;
    }
  }
}
</style>
